<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { EntityCategory, EntitySummary, EntityTemplate } from '../../types/entities';
import type { ExportEntry, ExportOptions, ExportPageLayout } from '../../types/export';
import ExportPageSettings from './ExportPageSettings.vue';
import { defaultExportPageLayout, exportPageLayoutError } from '../../lib/exportPageLayout';
import ExportCatalog from './ExportCatalog.vue';
import ExportSelection from './ExportSelection.vue';
import { useExportOrdering } from '../../composables/useExportOrdering';

const props = withDefaults(
  defineProps<{
    entities: EntitySummary[];
    categories: EntityCategory[];
    templates: EntityTemplate[];
    entries: ExportEntry[];
    options: ExportOptions;
    title: string;
    count: number;
    busy: boolean;
    error: string;
    layout?: ExportPageLayout;
  }>(),
  { layout: defaultExportPageLayout }
);
const emit = defineEmits<{
  'update:entries': [value: ExportEntry[]];
  'update:options': [value: ExportOptions];
  'update:title': [value: string];
  'update:layout': [value: ExportPageLayout];
  close: [];
  export: [];
  chapters: [];
}>();
const dialog = ref<HTMLFormElement | null>(null);
const previousFocus = ref<HTMLElement | null>(null);
const limitError = ref('');
const layoutError = computed(() => exportPageLayoutError(props.layout));
const selectedIds = computed(() => props.entries.map((entry) => entry.entityId));
const { automaticOrder, reorder, restoreOrder } = useExportOrdering(props, (entries) =>
  emit('update:entries', entries)
);
function add(ids: string[]) {
  const missing = [...new Set(ids)].filter((id) => !selectedIds.value.includes(id));
  if (props.entries.length + missing.length > 500) {
    limitError.value = 'Bitte wähle höchstens 500 Einträge aus.';
    return;
  }
  limitError.value = '';
  emit('update:entries', [...props.entries, ...missing.map((entityId): ExportEntry => ({ kind: 'entity', entityId }))]);
}
function option(key: keyof ExportOptions, checked: boolean) {
  emit('update:options', { ...props.options, [key]: checked });
}
function keydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && !props.busy) {
    event.preventDefault();
    emit('close');
  }
  if (event.key !== 'Tab') return;
  const elements = [
    ...(dialog.value?.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]'
    ) ?? [])
  ];
  const first = elements[0];
  const last = elements[elements.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last?.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first?.focus();
  }
}
onMounted(() => {
  previousFocus.value = document.activeElement as HTMLElement | null;
  dialog.value?.querySelector<HTMLInputElement>('input')?.focus();
});
onUnmounted(() => previousFocus.value?.focus());
</script>

<template>
  <div class="modal-backdrop workspace-export-backdrop" @click.self="!busy && emit('close')" @keydown="keydown">
    <form
      ref="dialog"
      class="workspace-export-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="workspace-export-title"
      :aria-busy="busy"
      @submit.prevent="!layoutError && emit('export')"
    >
      <header>
        <div>
          <span class="eyebrow">PDF-Export</span>
          <h2 id="workspace-export-title">Deinen Export zusammenstellen</h2>
        </div>
        <button
          type="button"
          class="ghost"
          aria-label="Exportfenster schließen"
          :disabled="busy"
          @click="emit('close')"
        >
          ×
        </button>
      </header>
      <p>
        Kapitel, Szenen, Figuren, Orte und weitere Entitäten frei kombinieren. Exportiert wird die aktuelle
        Arbeitsfassung.
      </p>
      <div class="export-scroll">
        <fieldset :disabled="busy" class="export-content">
          <label class="export-title"
            >Dokumenttitel<input
              :value="title"
              maxlength="240"
              @input="emit('update:title', ($event.target as HTMLInputElement).value)"
          /></label>
          <ExportPageSettings :layout="layout" :busy="busy" @update="emit('update:layout', $event)" />
          <div class="export-toolbar">
            <button type="button" class="ghost" @click="emit('chapters')">Alle Kapitel in Erzählreihenfolge</button
            ><button
              type="button"
              class="ghost"
              @click="
                emit('update:entries', []);
                limitError = '';
              "
            >
              Auswahl leeren</button
            ><span>{{ count }} Entitäten im Dokument</span>
          </div>
          <div class="export-toolbar">
            <span>{{ automaticOrder ? 'Automatisch nach Kategorie und Art sortiert' : 'Eigene Reihenfolge' }}</span>
            <button type="button" class="ghost" @click="restoreOrder">Sinnvoll sortieren</button>
          </div>
          <div class="export-columns">
            <ExportCatalog
              :entities="entities"
              :categories="categories"
              :templates="templates"
              :selected-ids="selectedIds"
              :busy="busy"
              @add="add"
            /><ExportSelection
              :entries="entries"
              :entities="entities"
              :busy="busy"
              @update="emit('update:entries', $event)"
              @reorder="reorder"
            />
          </div>
          <div class="export-options">
            <label><input
              type="checkbox"
              :checked="options.hideSceneInformation"
              @change="option('hideSceneInformation', ($event.target as HTMLInputElement).checked)"
            />Szeneninformationen ausblenden (nur Kapitel- und Szenennamen sowie Text)</label>
          </div>
          <div class="export-options">
            <label
              ><input
                type="checkbox"
                :checked="options.includeSummary"
                @change="option('includeSummary', ($event.target as HTMLInputElement).checked)"
              />Zusammenfassungen</label
            ><label
              ><input
                type="checkbox"
                :checked="options.includeFields"
                @change="option('includeFields', ($event.target as HTMLInputElement).checked)"
              />Felder und Steckbriefe</label
            ><label
              ><input
                type="checkbox"
                :checked="options.includeProfiles"
                @change="option('includeProfiles', ($event.target as HTMLInputElement).checked)"
              />Figurenprofile</label
            >
          </div>
        </fieldset>
      </div>
      <p
        v-if="error || limitError || layoutError || count > 1000 || entries.length > 500"
        class="export-error"
        role="alert"
      >
        {{
          error ||
          limitError ||
          layoutError ||
          'Bitte verkleinere die Auswahl auf höchstens 500 Einträge und 1.000 Entitäten einschließlich Szenen.'
        }}
      </p>
      <div class="dialog-actions">
        <button type="button" class="ghost" :disabled="busy" @click="emit('close')">Abbrechen</button
        ><button type="submit" :disabled="busy || !!layoutError || !count || count > 1000 || entries.length > 500">
          {{ busy ? 'PDF wird erstellt …' : 'Als PDF exportieren' }}
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped src="./workspaceExport.css"></style>
